import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import "./CostDetailsForm.css";

// API file structure (from backend)
export interface ApiFileData {
  id: number;
  file_name: string;
  file_path: string;
  original_filename?: string;
}

// Form data for edit mode
export interface CostEditFormData {
  id?: number;
  cost_type_key: string;
  cost_description: string;
  cost_amount: string;
  currency: string;
  tax_applicable: boolean;
  tax_amount: string;
  total_cost_including_tax: string;
  vendor_service_provider_name: string;
  payment_status: string;
  cost_period_from: string;
  cost_period_to: string;
  existing_files: ApiFileData[];
  new_files: File[];
  deleted_file_ids: number[];
}

interface CostDetailsEditFormProps {
  isOpen: boolean;
  costTypeLabel: string;
  costTypeKey: string;
  onClose: () => void;
  onSave: (costTypeKey: string, data: CostEditFormData) => Promise<void>;
  initialData?: CostEditFormData;
  isLoading?: boolean;
}

const getInitialFormData = (costTypeKey: string): CostEditFormData => ({
  cost_type_key: costTypeKey,
  cost_description: "",
  cost_amount: "",
  currency: "",
  tax_applicable: true,
  tax_amount: "",
  total_cost_including_tax: "",
  vendor_service_provider_name: "",
  payment_status: "",
  cost_period_from: "",
  cost_period_to: "",
  existing_files: [],
  new_files: [],
  deleted_file_ids: [],
});

const CostDetailsEditForm: React.FC<CostDetailsEditFormProps> = ({
  isOpen,
  costTypeLabel,
  costTypeKey,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CostEditFormData>(
    initialData || getInitialFormData(costTypeKey)
  );
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const costPeriodToRef = useRef<HTMLInputElement | null>(null);

  // Reset form when costTypeKey changes or when initialData changes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialData || getInitialFormData(costTypeKey));
    }
  }, [costTypeKey, isOpen, initialData]);

  // Auto-calculate total cost when cost_amount or tax_amount changes
  useEffect(() => {
    const costAmount = parseFloat(formData.cost_amount) || 0;
    const taxAmount = formData.tax_applicable ? (parseFloat(formData.tax_amount) || 0) : 0;
    const total = costAmount + taxAmount;

    const roundedTotal = Math.round(total * 100) / 100;
    const formattedTotal = parseFloat(roundedTotal.toFixed(2)).toString();

    setFormData((prev) => ({
      ...prev,
      total_cost_including_tax: formattedTotal,
    }));
  }, [formData.cost_amount, formData.tax_amount, formData.tax_applicable]);

  const handleInputChange = (field: keyof CostEditFormData, value: any) => {
    // Clear tax_amount when tax_applicable is turned off
    if (field === "tax_applicable" && value === false) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        tax_amount: "",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.cost_description || !formData.cost_description.trim()) {
      toast.error("Please enter cost description");
      return false;
    }
    if (!formData.cost_amount || !formData.cost_amount.trim()) {
      toast.error("Please enter cost amount");
      return false;
    }
    if (formData.tax_applicable && (!formData.tax_amount || !formData.tax_amount.trim())) {
      toast.error("Please enter tax amount");
      return false;
    }
    if (!formData.vendor_service_provider_name || !formData.vendor_service_provider_name.trim()) {
      toast.error("Please enter vendor/service provider name");
      return false;
    }
    if (!formData.payment_status || !formData.payment_status.trim()) {
      toast.error("Please enter payment status");
      return false;
    }
    if (!formData.cost_period_from) {
      toast.error("Please select cost period from date");
      return false;
    }
    if (!formData.cost_period_to) {
      toast.error("Please select cost period to date");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      await onSave(costTypeKey, formData);
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialData || getInitialFormData(costTypeKey));
    onClose();
  };

  const hasChanges = (): boolean => {
    if (!initialData) return true;

    const fieldsToCompare: (keyof CostEditFormData)[] = [
      "cost_description",
      "cost_amount",
      "tax_applicable",
      "tax_amount",
      "vendor_service_provider_name",
      "payment_status",
      "cost_period_from",
      "cost_period_to",
    ];

    for (const field of fieldsToCompare) {
      if (formData[field] !== initialData[field]) {
        return true;
      }
    }

    // Check for file changes
    if (formData.new_files.length > 0 || formData.deleted_file_ids.length > 0) {
      return true;
    }

    return false;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="cost-details-form-wrapper">
      <div className="license-popup-overlay"></div>
      <div className="cost-details-popup">
        <div className="popup-header d-flex justify-content-between align-items-start">
          <h5 className="mb-0 fw-semibold">Edit Cost Details: {costTypeLabel}</h5>
          <button
            className="btn-close"
            aria-label="Close"
            onClick={handleCancel}
            disabled={isSaving || isLoading}
          ></button>
        </div>

        <hr className="mb-3 mt-0" />

        <div className="popup-body">
          {/* Cost Description */}
          <div className="mb-3">
            <label className="form-label">Cost Description<span>*</span></label>
            <textarea
              className="form-control cost-description-textarea"
              placeholder="Enter cost description"
              value={formData.cost_description}
              onChange={(e) => handleInputChange("cost_description", e.target.value)}
              style={{ minHeight: "100px"}}
              disabled={isSaving || isLoading}
            />
          </div>

          {/* Cost Amount */}
          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill">
              <label className="form-label">
                Cost Amount<span>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Add cost amount"
                value={formData.cost_amount}
                onChange={(e) => handleInputChange("cost_amount", e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isSaving || isLoading}
                min="1"
              />
            </div>
          </div>

          {/* Tax Applicable Toggle */}
          <div className="mb-3 d-flex align-items-center justify-content-between">
            <div>
              <label className="form-label mb-0">Tax Applicable</label>
              <p className="mb-0 text-gray-12 text-sm">
                Enable this toggle if this cost is including tax
              </p>
            </div>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="taxApplicableEdit"
                checked={formData.tax_applicable}
                onChange={(e) => handleInputChange("tax_applicable", e.target.checked)}
                disabled={isSaving || isLoading}
              />
            </div>
          </div>

          {/* Tax Amount - Only show when tax_applicable is true */}
          {formData.tax_applicable && (
            <div className="flex-fill mb-3">
              <label className="form-label">
                Tax Amount<span>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Add tax amount"
                value={formData.tax_amount}
                onChange={(e) => handleInputChange("tax_amount", e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isSaving || isLoading}
                min="1"
              />
            </div>
          )}

          {/* Total Cost Including Tax - Auto calculated, read-only */}
          <div className="mb-3">
            <label className="form-label">
              Total Cost {formData.tax_applicable ? "(Including Tax)" : ""}<span>*</span>
            </label>
            <input
              type="number"
              className="form-control bg-light"
              placeholder="Auto calculated total cost"
              value={formData.total_cost_including_tax}
              onWheel={(e) => e.currentTarget.blur()}
              readOnly
              disabled
            />
          </div>

          {/* Vendor/Service Provider Name */}
          <div className="mb-3">
            <label className="form-label">
              Vendor/Service Provider Name<span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Add vendor/service provider name"
              value={formData.vendor_service_provider_name}
              onChange={(e) => handleInputChange("vendor_service_provider_name", e.target.value)}
              disabled={isSaving || isLoading}
            />
          </div>

          {/* Payment Status */}
          <div className="mb-3">
            <label className="form-label">
              Payment Status<span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Add payment status"
              value={formData.payment_status}
              onChange={(e) => handleInputChange("payment_status", e.target.value)}
              disabled={isSaving || isLoading}
            />
          </div>

          {/* Cost Period */}
          <div className="mb-3">
            <label className="form-label">Cost Period<span>*</span></label>
            <div className="d-flex gap-3 align-items-center">
              <input
                type="date"
                className="form-control"
                value={formData.cost_period_from}
                max={formData.cost_period_to || undefined}
                onChange={(e) => {
                  handleInputChange("cost_period_from", e.target.value);
                  if (e.target.value && costPeriodToRef.current) {
                    const toInput = costPeriodToRef.current;
                    setTimeout(() => {
                      if (toInput) {
                        toInput.showPicker();
                      }
                    }, 100);
                  }
                }}
                disabled={isSaving || isLoading}
              />
              <span className="text-gray-12">To</span>
              <input
                ref={costPeriodToRef}
                type="date"
                className="form-control"
                value={formData.cost_period_to}
                min={formData.cost_period_from || undefined}
                onChange={(e) => handleInputChange("cost_period_to", e.target.value)}
                disabled={isSaving || isLoading || !formData.cost_period_from}
              />
            </div>
          </div>

          {/* Upload Invoice / Supporting Document */}
          {/* <div className="mb-3">
            <label className="form-label">Upload Invoice / Supporting Document<span>*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.svg,.pdf"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              multiple
              disabled={isSaving || isLoading}
            />

            {totalVisibleFiles === 0 && (
              <div
                className="p-4 text-center upload-box"
                onClick={handleChooseFile}
                style={{ cursor: isSaving || isLoading ? "not-allowed" : "pointer" }}
              >
                <div className="mb-2">
                  <img src={cloudUpload} alt="cloudUpload" className="me-2" />
                </div>
                <p className="mb-2 text-md text-gray-12">
                  Click to browse and upload
                </p>
                <button
                  type="button"
                  className="mb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChooseFile();
                  }}
                  disabled={isSaving || isLoading}
                >
                  Choose File
                </button>
                <div>
                  <small className="text-gray-12 d-block">
                    Supported formats (Up to 15 MB per file, max 5 files): JPG, PNG, WEBP, SVG, PDF, JPEG
                  </small>
                </div>
              </div>
            )}

            {visibleExistingFiles.map((file) => (
              <div
                key={`existing-${file.id}`}
                className="upload-successful position-relative mb-2"
              >
                <div
                  className="close-button position-absolute"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveExistingFile(file.id);
                  }}
                  style={{ cursor: isSaving || isLoading ? "not-allowed" : "pointer" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                      stroke="#757575"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="p-3 d-flex align-items-center justify-content-between">
                  <p className="text-sm m-0 ellipse">{file.original_filename || file.file_name}</p>
                  <Icon name="cloud" />
                </div>
              </div>
            ))}

            {formData.new_files.map((file, index) => (
              <div
                key={`new-${index}`}
                className="upload-successful position-relative mb-2"
              >
                <div
                  className="close-button position-absolute"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveNewFile(index);
                  }}
                  style={{ cursor: isSaving || isLoading ? "not-allowed" : "pointer" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                      stroke="#757575"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="p-3 d-flex align-items-center">
                  <p className="text-sm m-0 ellipse">{file.name}</p>
                </div>
              </div>
            ))}

            {totalVisibleFiles > 0 && totalVisibleFiles < 5 && (
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 mt-2"
                style={{ color: "var(--royal-blue)", fontSize: "14px" }}
                onClick={handleChooseFile}
                disabled={isSaving || isLoading}
              >
                + Add more files
              </button>
            )}
          </div> */}
        </div>

        <hr className="my-3" />

        {/* Footer Buttons */}
        <div className="popup-footer d-flex gap-3">
          <button
            type="button"
            className="button-secondary flex-fill"
            onClick={handleCancel}
            disabled={isSaving || isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button-primary flex-fill"
            onClick={handleSave}
            disabled={isSaving || isLoading || !hasChanges()}
          >
            {isSaving || isLoading ? "Saving..." : "Done"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CostDetailsEditForm;
